import { UserRoles } from '@models/user.model';
import { useAppSelector } from '@redux/hooks';

// Global env-var kill-switches. Each entry pairs a feature key with an env var
// that must be the literal string "true" for the feature to be visible.
// Unset (or any other value) hides the feature regardless of the user's role.
// Used to ship work-in-progress modules behind a deploy-time flag.
const FEATURE_ENV_FLAGS: Partial<Record<string, string | undefined>> = {
  INVOICES: process.env.REACT_APP_FEATURE_INVOICES_ENABLED,
};

// Define which roles can access each feature
const FEATURE_PERMISSIONS: Record<string, UserRoles[]> = {
  // Document Management
  DOCUMENTS: [UserRoles.OWNER, UserRoles.ADMIN, UserRoles.PROJECT_MANAGER],

  BUDGETS: [UserRoles.OWNER, UserRoles.ADMIN, UserRoles.PROJECT_MANAGER],

  INVOICES: [UserRoles.OWNER, UserRoles.ADMIN, UserRoles.PROJECT_MANAGER],

  // Templates. Story-107: task template import/export is a planning/scheduling capability —
  // SUPERINTENDENT is included alongside the existing OWNER/ADMIN/PROJECT_MANAGER grant.
  // SUPER_ADMIN is deliberately not added here, consistent with USERS/CLIENTS/CREWS/etc.
  TEMPLATES: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],

  // Core Features. Sprint 43, story-223: CLIENT is listed so the sidebar shows Dashboard, but it
  // gets its own checklist-driven client home there (`DashboardEntry`), not the superintendent
  // dashboard.
  DASHBOARD: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
    UserRoles.CLIENT,
  ],
  SCHEDULE: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],
  // Sprint 25, story-128. CLIENT joins REVIEWER on the one feature both external-viewer roles
  // may reach.
  PROJECTS: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
    UserRoles.REVIEWER,
    UserRoles.CLIENT,
  ],
  CREWS: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],
  USERS: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],
  CLIENTS: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],
  SUBCONTRACTORS: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],
  MAP_VIEW: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],
  INCIDENTS: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],
  WAREHOUSE: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],

  GEOFENCES: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],

  // Billing (Owner only)
  BILLING: [UserRoles.OWNER],

  BUDGET_TAB: [UserRoles.OWNER, UserRoles.ADMIN, UserRoles.PROJECT_MANAGER],

  // Super Admin Features
  ORGANIZATIONS: [UserRoles.SUPER_ADMIN],
  SUBSCRIPTION_PLANS: [UserRoles.SUPER_ADMIN],
  SUPER_ADMIN_NOTIFICATIONS: [UserRoles.SUPER_ADMIN],
  PLATFORM_STATUS: [UserRoles.SUPER_ADMIN],
  STANDALONE_INVOICE: [UserRoles.SUPER_ADMIN],

  // ATAV / Brightstar field capture. Reviewers are here for the Brightstar CSV
  // and see the whole tab read-only — the page itself hides every write control.
  FIELD_CAPTURE_ADMIN: [
    UserRoles.SUPER_ADMIN,
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.REVIEWER,
  ],

  TUTORIALS: [
    UserRoles.SUPER_ADMIN,
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],

  // Sprint 32, story-165. Ask-your-project assistant. SUPER_ADMIN deliberately excepted from the
  // web sidebar per the existing org-feature convention (matching TEMPLATES/PROJECTS/etc. above),
  // even though the backend's `@HasPermission(ASSISTANT_ROLES)` gate allows it.
  ASSISTANT: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
  ],

  // Sprint 33, story-169/170. End-of-day site report. Deliberately all-role (not just
  // SUPERINTENDENT+) — D1: "all nine roles get a report". REVIEWER/CLIENT included per the
  // feature's own design; CREW_LEAD/EMPLOYEE reach this only through mobile (mobile-only rule).
  DAILY_SITE_REPORTS: [
    UserRoles.OWNER,
    UserRoles.ADMIN,
    UserRoles.PROJECT_MANAGER,
    UserRoles.SUPERINTENDENT,
    UserRoles.REVIEWER,
    UserRoles.CLIENT,
  ],

  // Components (for UI showcase)
  COMPONENTS:
    process.env.REACT_APP_ENV === 'development'
      ? [
          UserRoles.SUPER_ADMIN,
          UserRoles.OWNER,
          UserRoles.ADMIN,
          UserRoles.PROJECT_MANAGER,
          UserRoles.SUPERINTENDENT,
        ]
      : [],

  // Settings
  SETTINGS_MIN_VERSION: [UserRoles.SUPER_ADMIN],
  SIDEBAR_ACTIONS: [UserRoles.SUPER_ADMIN],
};
/**
 * Sprint 43, story-223. The deploy-time env kill-switch alone, without a role check. The CLIENT
 * home needs this: CLIENT is (deliberately) not in `FEATURE_PERMISSIONS.INVOICES`, but the
 * Invoices triage pill is still gated on the same env flag plus the checklist's `priceTotals`.
 * Features with no env flag are always enabled here.
 */
export const isFeatureEnvEnabled = (
  feature: keyof typeof FEATURE_PERMISSIONS,
): boolean =>
  !(feature in FEATURE_ENV_FLAGS) || FEATURE_ENV_FLAGS[feature] === 'true';

/**
 * Check if a feature is enabled for a specific user role
 * @param feature - The feature name
 * @param userRole - The user's role
 * @returns boolean indicating if the feature is enabled
 */
export const canAccess = (
  feature: keyof typeof FEATURE_PERMISSIONS,
  userRole: UserRoles | string | null,
): boolean => {
  if (!userRole) return false;
  if (feature in FEATURE_ENV_FLAGS && FEATURE_ENV_FLAGS[feature] !== 'true') return false;
  return FEATURE_PERMISSIONS[feature]?.includes(userRole as UserRoles) || false;
};

/**
 * React hook to check feature access using current user role from Redux
 */
export const useFeatureAccess = () => {
  const { role } = useAppSelector((state) => state.user);

  return {
    canAccess: (feature: keyof typeof FEATURE_PERMISSIONS) => {
      const hasAccess = canAccess(feature, role);
      return hasAccess;
    },
    role,
  };
};
